import { Box, Checkbox, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

/**
 * A tappable row inside a chat: an offer, or the way on.
 *
 * An offer is not a message. On floral white with a neutral border it was the
 * same surface as a bubble and the same surface as a card, so a row you could
 * tap sat in the transcript looking like something that had been said. The
 * lightest marine with a step darker around it reads as a control at a glance
 * and still sits quietly under the question.
 *
 * `go` is the last thing in a turn, the way on rather than a thing on offer, and
 * it takes lime instead.
 *
 * `selectable` puts a checkbox on the row, for a question that takes more than
 * one answer. Without it a pick-several looks exactly like a pick-one: the rows
 * are identical, so the only thing saying a second answer is allowed is the
 * "Continue when you're done" underneath them, which is read after the choice
 * has already been made.
 *
 * Wrapped, not a variant: it carries the tone and the icon colour together, and
 * it is the one object every chat in the app should use for this. Lifted out of
 * ChatV2, where it started, so the intake and the pre-sign-up screens cannot
 * drift into two teals.
 */
const ChatActionRow = ({
  label,
  icon,
  tone = 'offer',
  selectable = false,
  selected = false,
  onClick,
}: {
  label: string;
  icon?: string;
  tone?: 'offer' | 'go';
  /** Draw a checkbox, for a row belonging to a question that takes several. */
  selectable?: boolean;
  selected?: boolean;
  onClick?: () => void;
}) => {
  const go = tone === 'go';

  return (
    <Stack
      onClick={onClick}
      sx={{
        alignItems: 'center',
        px: 2,
        py: 1.25,
        bgcolor: go ? 'custom.limeGreen.200' : 'custom.marineGreen.50',
        border: 1,
        borderColor: go ? 'primary.main' : 'custom.marineGreen.100',
        borderRadius: `${radius.md}px`,
        cursor: 'pointer',
        // Tapping one of these is how a closed question gets answered, so it has
        // to feel like a button being pressed rather than a message being
        // touched.
        //
        // The press is the state that matters, because on a phone there is no
        // hover: the border goes marine and the row gives a per cent under the
        // thumb.
        //
        // The fill does not darken at all. One step down the marine ramp took
        // the charcoal label from about 7:1 to under 5:1 and the row looked
        // muddy under the pointer. So the border carries the hover and the
        // press, a darker marine against the same light fill, with the
        // squeeze on press. Which is also all the lime row has, its ramp being
        // flat.
        transition:
          'background-color 120ms ease, border-color 120ms ease, transform 120ms ease',
        '&:hover': {
          borderColor: go ? 'primary.dark' : 'custom.marineGreen.300',
        },
        '&:active': {
          borderColor: 'primary.main',
          transform: 'scale(0.99)',
        },
      }}
      direction='row'
      spacing={1.5}
    >
      {/* Marine, not coral. Coral at step 500 on the lightest marine is barely
          there: the row went teal and the icon stayed the colour it had been
          when the row was floral white.

          Absent where the row is an answer: a tick or a camera says what
          tapping does, and "Yes, I'm helping look after someone" already
          does. */}
      {selectable ? (
        <Checkbox checked={selected} color='primary' sx={{ p: 0 }} />
      ) : (
        icon && (
          <Box sx={{ color: 'primary.main', display: 'flex' }}>
            <Icon icon={icon as never} fontSize={16} />
          </Box>
        )
      )}
      <Typography variant='body1'>{label}</Typography>
    </Stack>
  );
};

ChatActionRow.displayName = 'ChatActionRow';
export default ChatActionRow;
